import React, { useEffect, useState } from 'react';
import { UserConfig } from '../../lib/types';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { userConfigApi } from '../../lib/api';

interface UserConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  config: UserConfig | null;
  onSubmit: (data: UserConfig) => Promise<void>;
  mode: 'create' | 'edit';
  isSubmitting?: boolean;
}

const emptyConfig: UserConfig = {
  id: '',
  name: '',
  email: '',
  bybitTestnetApiKey: '',
  bybitTestnetApiSecret: '',
  bybitProductionApiKey: '',
  bybitProductionApiSecret: '',
  testnetApiKey: '',
  productionApiKey: '',
  proxyHost: '',
  proxyPort: undefined,
  proxyUsername: '',
  proxyPassword: '',
  longConfig: '',
  shortConfig: '',
  tradingEnabled: false,
  cognitoUserId: '',
  notificationEmail: '',
  notificationsEnabled: false,
  notificationStatus: 'disabled',
  snsSubscriptionArn: undefined,
  reportsEnabled: false,
  sesStatus: 'disabled',
  sesIdentityArn: undefined,
};

export const UserConfigDialog: React.FC<UserConfigDialogProps> = ({
  isOpen,
  onClose,
  config,
  onSubmit,
  mode,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<UserConfig>(emptyConfig);
  const [formError, setFormError] = useState<string | null>(null);
  const [creatingCognito, setCreatingCognito] = useState(false);
  const [cognitoStatus, setCognitoStatus] = useState<string | null>(null);
  const [cognitoError, setCognitoError] = useState<string | null>(null);

  useEffect(() => {
    if (config) {
      setFormData({
        ...emptyConfig,
        ...config,
        notificationEmail: config.notificationEmail ?? config.email ?? '',
      });
    } else {
      setFormData(emptyConfig);
    }
    setFormError(null);
    setCognitoStatus(null);
    setCognitoError(null);
  }, [config, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.id.trim()) {
      setFormError('User ID is required.');
      return;
    }
    if (!formData.name.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (mode === 'create' && !(formData.email ?? '').trim()) {
      setFormError('Email is required for new users.');
      return;
    }
    setFormError(null);

    await onSubmit({
      ...formData,
      proxyPort: formData.proxyPort || undefined,
      tradingEnabled: formData.tradingEnabled ?? false,
    });
    onClose();
  };

  const handleChange = (field: keyof UserConfig) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleCheckboxChange = (field: keyof UserConfig) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.checked,
    }));
  };

  const handleCreateCognitoUser = async () => {
    if (!(formData.email ?? '').trim() || !formData.name) {
      setCognitoError('Name and email are required to create a Cognito user.');
      return;
    }

    setCognitoError(null);
    setCognitoStatus(null);
    setCreatingCognito(true);
    try {
      const response = await userConfigApi.createCognitoUser({
        email: formData.email ?? '',
        name: formData.name,
      });
      setFormData(prev => ({
        ...prev,
        cognitoUserId: response.data.cognitoUserId,
      }));
      setCognitoStatus('Cognito user created and linked successfully.');
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      const message = axiosError?.response?.data?.error || (error instanceof Error ? error.message : 'Failed to create Cognito user');
      setCognitoError(message);
    } finally {
      setCreatingCognito(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {mode === 'create' ? 'Add Trading Configuration' : `Edit Trading Configuration - ${config?.name}`}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">User Details</h3>
            <div>
              <Label htmlFor="userId">User ID</Label>
              <Input
                id="userId"
                type="text"
                value={formData.id}
                onChange={handleChange('id')}
                placeholder="Enter unique user ID"
                disabled={mode === 'edit'}
              />
            </div>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={handleChange('name')}
                placeholder="Enter display name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange('email')}
                placeholder="user@example.com"
              />
            </div>

            <div>
              <Label htmlFor="notificationEmail">Notification Email</Label>
              <Input
                id="notificationEmail"
                type="email"
                value={formData.notificationEmail || ''}
                onChange={handleChange('notificationEmail')}
                placeholder="Alerts and reports will be sent here"
              />
              <p className="text-xs text-gray-500 mt-1">Used for notifications and scheduled reports.</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <Label>Cognito User</Label>
                <p className="text-sm text-gray-600">
                  {formData.cognitoUserId ? formData.cognitoUserId : 'Not linked'}
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCreateCognitoUser}
                disabled={creatingCognito}
              >
                {creatingCognito ? 'Creating...' : formData.cognitoUserId ? 'Recreate Cognito User' : 'Create Cognito User'}
              </Button>
            </div>
            {cognitoStatus && <p className="text-sm text-green-600">{cognitoStatus}</p>}
            {cognitoError && <p className="text-sm text-red-600">{cognitoError}</p>}
            <div className="flex items-center gap-2">
              <input
                id="tradingEnabled"
                type="checkbox"
                checked={formData.tradingEnabled ?? false}
                onChange={handleCheckboxChange('tradingEnabled')}
                disabled={!formData.cognitoUserId}
                className="h-4 w-4"
              />
              <Label htmlFor="tradingEnabled">Trading Enabled</Label>
            </div>
            {!formData.cognitoUserId && (
              <p className="text-xs text-gray-500">Link a Cognito user to enable trading.</p>
            )}

            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  id="notificationsEnabled"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={formData.notificationsEnabled ?? false}
                  onChange={handleCheckboxChange('notificationsEnabled')}
                />
                Email Notifications
                {formData.notificationStatus && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                    {formData.notificationStatus}
                  </span>
                )}
              </label>
              {formData.notificationStatus === 'pending' && (
                <p className="text-xs text-amber-600">Pending confirmation. Check the inbox at {formData.notificationEmail}.</p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  id="reportsEnabled"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={formData.reportsEnabled ?? false}
                  onChange={handleCheckboxChange('reportsEnabled')}
                />
                Performance Reports
                {formData.sesStatus && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                    {formData.sesStatus}
                  </span>
                )}
              </label>
              {formData.sesStatus === 'pending' && (
                <p className="text-xs text-amber-600">SES verification pending. Check the inbox at {formData.notificationEmail}.</p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Testnet Configuration</h3>
            <div>
              <Label htmlFor="bybitTestnetApiKey">Bybit Testnet API Key</Label>
              <Input
                id="bybitTestnetApiKey"
                type="text"
                value={formData.bybitTestnetApiKey}
                onChange={handleChange('bybitTestnetApiKey')}
                placeholder="Enter Bybit testnet API key"
              />
            </div>
            <div>
              <Label htmlFor="bybitTestnetApiSecret">Bybit Testnet API Secret</Label>
              <Input
                id="bybitTestnetApiSecret"
                type="password"
                value={formData.bybitTestnetApiSecret}
                onChange={handleChange('bybitTestnetApiSecret')}
                placeholder="Enter Bybit testnet API secret"
              />
            </div>
            <div>
              <Label htmlFor="testnetApiKey">Trading Webhook Testnet API Key</Label>
              {mode === 'create' ? (
                <div className="text-sm text-gray-600">Automatically generated when the user is created.</div>
              ) : (
                <Input
                  id="testnetApiKey"
                  type="text"
                  value={formData.testnetApiKey}
                  onChange={handleChange('testnetApiKey')}
                  placeholder="Enter trading webhook testnet API key"
                />
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Production Configuration</h3>
            <div>
              <Label htmlFor="bybitProductionApiKey">Bybit Production API Key</Label>
              <Input
                id="bybitProductionApiKey"
                type="text"
                value={formData.bybitProductionApiKey}
                onChange={handleChange('bybitProductionApiKey')}
                placeholder="Enter Bybit production API key"
              />
            </div>
            <div>
              <Label htmlFor="bybitProductionApiSecret">Bybit Production API Secret</Label>
              <Input
                id="bybitProductionApiSecret"
                type="password"
                value={formData.bybitProductionApiSecret}
                onChange={handleChange('bybitProductionApiSecret')}
                placeholder="Enter Bybit production API secret"
              />
            </div>
            <div>
              <Label htmlFor="productionApiKey">Trading Webhook Production API Key</Label>
              {mode === 'create' ? (
                <div className="text-sm text-gray-600">Automatically generated when the user is created.</div>
              ) : (
                <Input
                  id="productionApiKey"
                  type="text"
                  value={formData.productionApiKey}
                  onChange={handleChange('productionApiKey')}
                  placeholder="Enter trading webhook production API key"
                />
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Proxy Configuration (Optional)</h3>
            <div>
              <Label htmlFor="proxyHost">Proxy Host</Label>
              <Input
                id="proxyHost"
                type="text"
                value={formData.proxyHost || ''}
                onChange={handleChange('proxyHost')}
                placeholder="e.g., 91.124.203.120"
              />
            </div>
            <div>
              <Label htmlFor="proxyPort">Proxy Port</Label>
              <Input
                id="proxyPort"
                type="number"
                value={formData.proxyPort || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  proxyPort: e.target.value ? parseInt(e.target.value, 10) : undefined,
                }))}
                placeholder="e.g., 12323"
              />
            </div>
            <div>
              <Label htmlFor="proxyUsername">Proxy Username</Label>
              <Input
                id="proxyUsername"
                type="text"
                value={formData.proxyUsername || ''}
                onChange={handleChange('proxyUsername')}
                placeholder="Enter proxy username"
              />
            </div>
            <div>
              <Label htmlFor="proxyPassword">Proxy Password</Label>
              <Input
                id="proxyPassword"
                type="password"
                value={formData.proxyPassword || ''}
                onChange={handleChange('proxyPassword')}
                placeholder="Enter proxy password"
              />
            </div>
          </div>

          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : mode === 'create' ? 'Add User' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
